import React, { useCallback, useEffect, useState } from 'react';
import { Auth as _Auth } from '~/amplify';
import { navigate, useLocation, WindowLocation } from '@gatsbyjs/reach-router';
import getEmailAndPasswordFromMagicLink from '~/components/page/Auth/Login/utils/getEmailAndPasswordFromMagicLink';
import type { WindowState } from '~/components/page/Auth/Login';
import useLogout from '~/hooks/useLogout';
import useIsMounted from '~/hooks/useIsMounted';

type CognitoUser = {
  username: string;
  /** Currently we only have one group: ['DH_CROCS_ADMIN'] (internal DH admin, e.g can impersonate) */
  cognitoGroups: Array<string>;
};

type AuthContextProps = {
  isLoggedIn: boolean;
  ensureLogin: (state?: { from?: string }) => void;
  user: CognitoUser | null;
  /** Checks if the user is DH_CROCS_ADMIN */
  isDHAdmin: boolean;
};

export const AuthContext = React.createContext<AuthContextProps>({
  isLoggedIn: false,
  user: null,
  ensureLogin: () => {},
  isDHAdmin: false,
});

type Props = {
  children: React.ReactNode;
};

const Auth: React.FCC<Props> = ({ children }) => {
  const [isValidatingLogin, setValidateLogin] = useState(true);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const logout = useLogout();
  const isMounted = useIsMounted();

  const ensureLogin = useCallback(() => setValidateLogin(true), []);
  const location = useLocation() as WindowLocation<
    { from?: string } | undefined
  >;
  const isLoggedIn = user !== null;
  const [magicLinkProcessed, setMagicLinkProcessed] = useState(false);

  useEffect(() => {
    if (!isMounted()) return;

    const verifyCredentials = async () => {
      if (location) {
        const credentials = getEmailAndPasswordFromMagicLink(
          location as WindowLocation<WindowState>,
        );

        if (credentials.email && credentials.password && !magicLinkProcessed) {
          // If magic link credentials exist, log out current user
          try {
            await logout();

            setMagicLinkProcessed(true);
            setValidateLogin(false);
            setUser(null);
          } catch (error) {
            // Handle error gracefully without logging
            setValidateLogin(false); // Ensure we don't get stuck in validating state
          }
          return;
        }
      }

      try {
        const currentUser = await _Auth.getCurrentUser();
        const session = await _Auth.fetchAuthSession();

        const cognitoGroups =
          (session.tokens?.accessToken?.payload?.[
            'cognito:groups'
          ] as Array<string>) || [];

        const user: CognitoUser = {
          username: currentUser.username,
          cognitoGroups,
        };

        setUser(user);
        setValidateLogin(false);
      } catch (error) {
        // User is not authenticated
        setUser(null);
        setValidateLogin(false);
      }
    };

    if (isValidatingLogin) {
      void verifyCredentials();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidatingLogin]);

  if (isValidatingLogin) return null;

  const isAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/verify') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/setup-newpassword') ||
    location.pathname.startsWith('/setup-user-details');

  if (isAuthRoute && isLoggedIn) {
    /**
     * We are logged in, but are on a auth route.
     * Redirect to the dashboard, or the page they originated from
     * before they went through the auth process.
     *
     * E.g. arrive at /tasks?officeId=null (but are unauthorised) -> /login -> /tasks?officeId=null
     */
    void navigate(location.state?.from ?? '/-/', { replace: true }).then(() => {
      ensureLogin();
    });

    return null;
  }

  if (!isAuthRoute && !isLoggedIn) {
    /**
     * The user is not logged in and not on an auth route, we redirect them to /login
     *
     * We set the from state to redirect back to after the login
     * has happened. Except when we are on the logout route.
     * Cause endless loop.
     */
    const state = location.pathname.startsWith('/logout')
      ? {}
      : { from: location.pathname + location.search };

    void navigate('/login', {
      replace: true,
      state,
    });
    return null;
  }

  const isDHAdmin = user?.cognitoGroups.includes('DH_CROCS_ADMIN') || false;

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: user != null,
        user,
        ensureLogin,
        isDHAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default Auth;
